import { BuilderActionPartial } from "@src/features/libraryItem/contexts/BuilderActionContext";
import { cloneDeep } from "lodash";
import { isRichTextEmpty } from "@src/features/libraryItem/course/utils/utils";
import { SkillBuilder_SkillVersionFragment } from "../SkillDetailPage.generated";
import { VerificationStepType } from "@src/types.generated";
import { isConditionValidForParentType } from "./utils";
import { triggerIsInvalid } from "./triggers";

export type SkillValidationErrors = {
  hasNoName: boolean;
  stepsWithEmptyText: Set<string>;
  noVerificationSteps: boolean;
  sectionsWithNoSteps: Set<string>;
  sectionsWithErrors: Set<string>;
  stepsWithInvalidConditionalStepGroups: Set<string>;
  stepsWithInvalidTrigger: Set<string>;
  // Multiple choice steps with zero response options.
  stepsWithNoResponseOptions: Set<string>;
  // Step response options whose label text is empty (any step type that has
  // response options — currently MC and Checkbox both render text inputs).
  responseOptionsWithEmptyText: Set<string>;
};

export const emptySkillValidationErrors = (): SkillValidationErrors => {
  return {
    hasNoName: false,
    stepsWithEmptyText: new Set(),
    noVerificationSteps: false,
    sectionsWithNoSteps: new Set(),
    sectionsWithErrors: new Set(),
    stepsWithInvalidConditionalStepGroups: new Set(),
    stepsWithInvalidTrigger: new Set(),
    stepsWithNoResponseOptions: new Set(),
    responseOptionsWithEmptyText: new Set(),
  };
};

export const applyBuilderActionToSkillValidationErrors = (args: {
  validationErrors: SkillValidationErrors;
  action: BuilderActionPartial;
}): SkillValidationErrors => {
  const sba = args.action.payload.skillBuilderAction;
  if (!sba) return args.validationErrors;
  const validationErrors = cloneDeep(args.validationErrors);

  if (sba.setStepTextAction) {
    validationErrors.stepsWithEmptyText.delete(sba.setStepTextAction.id);
    validationErrors.sectionsWithErrors.clear();
    validationErrors.sectionsWithNoSteps.clear();
  } else if (sba.setSkillNameAction) {
    validationErrors.hasNoName = false;
  } else if (sba.addStepAction) {
    validationErrors.noVerificationSteps = false;
    if (sba.addStepAction.step.type !== VerificationStepType.SectionBreak) {
      validationErrors.sectionsWithNoSteps.clear();
      validationErrors.sectionsWithErrors.clear();
    }
  } else if (sba.removeStepAction) {
    validationErrors.stepsWithEmptyText.delete(sba.removeStepAction.id);
    validationErrors.sectionsWithNoSteps.delete(sba.removeStepAction.id);
    validationErrors.sectionsWithErrors.delete(sba.removeStepAction.id);
    validationErrors.stepsWithInvalidConditionalStepGroups.delete(
      sba.removeStepAction.id,
    );
    validationErrors.stepsWithInvalidTrigger.delete(sba.removeStepAction.id);
    validationErrors.stepsWithNoResponseOptions.delete(sba.removeStepAction.id);
    validationErrors.sectionsWithErrors.clear();
    validationErrors.sectionsWithNoSteps.clear();
  } else if (sba.setStepTypeAction) {
    validationErrors.stepsWithNoResponseOptions.delete(
      sba.setStepTypeAction.id,
    );
  } else if (sba.addStepResponseOptionAction) {
    validationErrors.stepsWithNoResponseOptions.delete(
      sba.addStepResponseOptionAction.stepId,
    );
  } else if (sba.setStepResponseOptionTextAction) {
    validationErrors.responseOptionsWithEmptyText.delete(
      sba.setStepResponseOptionTextAction.stepResponseOptionId,
    );
  } else if (sba.removeStepResponseOptionAction) {
    validationErrors.responseOptionsWithEmptyText.delete(
      sba.removeStepResponseOptionAction.stepResponseOptionId,
    );
  }

  return validationErrors;
};

export const validateSkillVersion = (
  sv: SkillBuilder_SkillVersionFragment,
): SkillValidationErrors => {
  const ret = emptySkillValidationErrors();
  if (sv.verificationSteps.length === 0) {
    ret.noVerificationSteps = true;
  } else {
    const steps = sv.verificationSteps;
    const filteredSteps = steps.filter(
      (s) => s.type !== VerificationStepType.PassFail,
    );

    filteredSteps.forEach((s) => {
      if (isRichTextEmpty(s.text?.en || "")) {
        ret.stepsWithEmptyText.add(s.id);
      }
      if (s.childConditionalStepGroups.length > 0) {
        const hasInvalidGroup = s.childConditionalStepGroups.some(
          (group) => !isConditionValidForParentType(group, s),
        );
        if (hasInvalidGroup) {
          ret.stepsWithInvalidConditionalStepGroups.add(s.id);
        }
      }
      if (s.trigger && triggerIsInvalid(s.trigger, s)) {
        ret.stepsWithInvalidTrigger.add(s.id);
      }
      if (s.type === VerificationStepType.MultipleChoice) {
        if (s.responseOptions.length === 0) {
          ret.stepsWithNoResponseOptions.add(s.id);
        }
        for (const option of s.responseOptions) {
          if (isRichTextEmpty(option.text?.en || "")) {
            ret.responseOptionsWithEmptyText.add(option.id);
          }
        }
      }
    });

    let currentSectionId: string | null = null;
    let hasStepInCurrentSection = false;
    let currentSectionHasError = false;

    filteredSteps.forEach((step) => {
      if (step.type === VerificationStepType.SectionBreak) {
        if (currentSectionId) {
          if (!hasStepInCurrentSection) {
            ret.sectionsWithNoSteps.add(currentSectionId);
            ret.sectionsWithErrors.add(currentSectionId);
          } else if (currentSectionHasError) {
            ret.sectionsWithErrors.add(currentSectionId);
          }
        }
        currentSectionId = step.id;
        hasStepInCurrentSection = false;
        currentSectionHasError = false;
      } else if (currentSectionId) {
        hasStepInCurrentSection = true;
        const hasMcStepError =
          ret.stepsWithNoResponseOptions.has(step.id) ||
          step.responseOptions.some((o) =>
            ret.responseOptionsWithEmptyText.has(o.id),
          );
        const hasConditionalOrTriggerError =
          ret.stepsWithInvalidConditionalStepGroups.has(step.id) ||
          ret.stepsWithInvalidTrigger.has(step.id);
        if (
          ret.stepsWithEmptyText.has(step.id) ||
          hasMcStepError ||
          hasConditionalOrTriggerError
        ) {
          currentSectionHasError = true;
        }
      }
    });

    if (currentSectionId) {
      if (!hasStepInCurrentSection) {
        ret.sectionsWithNoSteps.add(currentSectionId);
        ret.sectionsWithErrors.add(currentSectionId);
      } else if (currentSectionHasError) {
        ret.sectionsWithErrors.add(currentSectionId);
      }
    }
  }

  if (isRichTextEmpty(sv.skill.libraryItem.name?.en || "")) {
    ret.hasNoName = true;
  }

  return ret;
};

export const skillValidationErrorCount = (
  errors: SkillValidationErrors,
): number => {
  return (
    errors.stepsWithEmptyText.size +
    errors.sectionsWithNoSteps.size +
    errors.stepsWithInvalidConditionalStepGroups.size +
    errors.stepsWithInvalidTrigger.size +
    errors.stepsWithNoResponseOptions.size +
    errors.responseOptionsWithEmptyText.size +
    (errors.hasNoName ? 1 : 0) +
    (errors.noVerificationSteps ? 1 : 0)
  );
};

export const hasSkillValidationErrors = (
  errors: SkillValidationErrors,
): boolean => {
  return skillValidationErrorCount(errors) > 0;
};
